/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Outlet } from 'react-router';
import type { Route } from './+types/_app.category.$categoryId';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchCategory } from '@/lib/api/categories.server';
import {
    resolveHorizontalNavigation,
    type HorizontalNavigationData,
} from '@/lib/category/horizontal-navigation.server';
import { getLogger } from '@/lib/logger.server';

export type CategoryLayoutLoaderData = {
    categoryId: string;
    /** Deferred: menu visibility + product-count eligibility must not block PLP TTFB. */
    horizontalNavigation: Promise<HorizontalNavigationData>;
};

/**
 * Category layout loader — owns horizontal navigation only.
 *
 * Isolated from the PLP index loader so sort/refine/offset revalidation does not
 * re-run the SCAPI eligibility fan-out. See `shouldRevalidate` in
 * `@/lib/revalidation/routes/category-layout`.
 */
export async function loader({ context, params: { categoryId } }: Route.LoaderArgs): Promise<CategoryLayoutLoaderData> {
    const logger = getLogger(context);
    logger.debug('Category layout: loader starting', { categoryId });

    let categoryData;
    try {
        categoryData = await fetchCategory(context, categoryId, 1);
    } catch (e) {
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }

    return {
        categoryId,
        horizontalNavigation: resolveHorizontalNavigation(context, categoryData),
    };
}

export { shouldRevalidate } from '@/lib/revalidation/routes/category-layout';

/**
 * Pathless shell for `/category/:categoryId`. Renders the PLP index (and any
 * future siblings) while keeping nav resolution on this route's revalidation
 * policy.
 */
export default function CategoryLayout(): ReactElement {
    return <Outlet />;
}
